import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLHeadingElement} variantTitle
 * @property {HTMLButtonElement} scrollToVariantPickerBtn
 * @property {HTMLButtonElement} addToCartBtn
 * @property {HTMLSpanElement} addToCartText
 * @property {HTMLSpanElement} addedToCartText
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCart extends Component {
  requiredRefs = [
    'variantTitle',
    'scrollToVariantPickerBtn',
    'addToCartBtn',
    'addToCartText',
    'addedToCartText',
  ];

  connectedCallback() {
    super.connectedCallback();

    this.addToCartLabel = this.getAttribute('data-add-to-cart-label');
    this.amount = this.getAttribute('data-amount');
    this.priceFormatter = new Intl.NumberFormat(Shopify.locale, {
      style: 'currency',
      currency: Shopify.currency.active,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    /** @type {HTMLElement | null} */
    this.productInformationEle = document.querySelector('.product-information');
    this.toggleStickyAtcVisibility();

    /** @type {HTMLElement | null} */
    this.variantPicker = document.querySelector('variant-picker');

    this.refs.addToCartBtn.addEventListener('click', this.handleAddToCart);

    if (!this.variantPicker) {
      this.refs.scrollToVariantPickerBtn.remove();
    } else {
      this.refs.scrollToVariantPickerBtn.addEventListener(
        'click',
        this.scrollToVariantPicker,
      );
    }

    /** @type {Component<{quantityInput: HTMLInputElement}> | null} */
    this.quantityInput = this.productInformationEle?.querySelector(
      'quantity-selector-component',
    );
    if (this.quantityInput) {
      const quantity = this.quantityInput.refs.quantityInput.value;
      this.handleQuantityUpdate(
        new QuantitySelectorUpdateEvent(Number(quantity)),
      );

      this.quantityInput.addEventListener(
        ThemeEvents.quantitySelectorUpdate,
        this.handleQuantityUpdate,
      );
    }
  }

  disconnectedCallback() {
    this.refs.scrollToVariantPickerBtn.removeEventListener(
      'click',
      this.scrollToVariantPicker,
    );

    if (this.productInformationObserver) {
      this.productInformationObserver.disconnect();
    }

    if (this.quantityInput) {
      this.quantityInput.removeEventListener(
        ThemeEvents.quantitySelectorUpdate,
        this.handleQuantityUpdate,
      );
    }
  }

  toggleStickyAtcVisibility() {
    if (!this.productInformationEle) {
      return;
    }

    this.productInformationObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.style.transform = 'translateY(-100%)';
            const disappearTimeout = setTimeout(() => {
              this.style.display = 'none';
              clearTimeout(disappearTimeout);
            }, 300);
          } else {
            this.style.display = 'flex';
            const appearTimeout = setTimeout(() => {
              this.style.transform = 'translateY(0)';
              clearTimeout(appearTimeout);
            }, 10);
          }
        });
      },
      {
        threshold: 0.1,
      },
    );
    this.productInformationObserver.observe(this.productInformationEle);
  }

  scrollToVariantPicker = () => {
    if (!this.variantPicker) {
      return;
    }

    window.scrollTo({
      top: this.variantPicker.offsetTop,
      behavior: 'smooth',
    });
  };

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  handleQuantityUpdate = (event) => {
    if (!this.amount || !this.addToCartLabel) {
      return;
    }

    const quantity = event.detail.quantity;
    const formattedAmount = this.priceFormatter
      ? this.priceFormatter.format(Number(this.amount) * quantity)
      : `${Shopify.currency.active}${this.amount}`;

    this.refs.addToCartText.textContent = this.addToCartLabel.replace(
      '{{ amount }}',
      formattedAmount,
    );
  };

  handleAddToCart = () => {
    if (!this.productInformationEle) {
      return;
    }

    /** @type {HTMLButtonElement | null} */
    const pdpAddToCartButton = this.productInformationEle.querySelector(
      'add-to-cart-component button[ref="addToCartButton"]',
    );
    if (!pdpAddToCartButton) {
      return;
    }

    this.refs.addToCartBtn.disabled = true;
    this.refs.addToCartText.style.display = 'none';
    this.refs.addedToCartText.style.display = 'flex';
    pdpAddToCartButton.click();

    const timeout = setTimeout(() => {
      this.refs.addToCartBtn.disabled = false;
      this.refs.addedToCartText.style.display = 'none';
      this.refs.addToCartText.style.display = 'flex';
      clearTimeout(timeout);
    }, 2000);
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAddToCart);
}
